// Phase 34 — Customer 360 (booking history + invoice list).
// Per the brief: NO chat threads, NO notes timeline, NO stats panel.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Edit, FileText,
  Mail, Phone, Building2, User as UserIcon, Star,
} from "lucide-react";
import CustomerDuplicateBanner from "@/components/CustomerDuplicateBanner";
import { DetailsPendingBadge } from "@/components/DetailsPendingBadge";

const PAGE_SIZE = 50;

const STATUS_TONES = {
  paid:                       "bg-emerald-100 text-emerald-800 border-emerald-200",
  awaiting_payment:           "bg-amber-100  text-amber-800  border-amber-200",
  overdue:                    "bg-red-100    text-red-800    border-red-200",
  write_off:                  "bg-slate-200  text-slate-700  border-slate-300",
  unable_to_collect:          "bg-slate-200  text-slate-700  border-slate-300",
  pending_manager_approval:   "bg-blue-100   text-blue-800   border-blue-200",
  // Booking statuses (subset).
  confirmed:                  "bg-emerald-100 text-emerald-800 border-emerald-200",
  draft:                      "bg-secondary  text-foreground border-border",
  cancelled:                  "bg-red-50     text-red-700    border-red-200",
  completed:                  "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function StatusPill({ status, testid }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const tone = STATUS_TONES[status] || "bg-secondary text-foreground border-border";
  return (
    <span
      className={"text-[10px] mono uppercase tracking-wider px-2 py-0.5 rounded-full border " + tone}
      data-testid={testid}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingSort, setBookingSort] = useState({ key: "scheduled_start", dir: "desc" });
  // Phase P.18 — GDPR anonymise dialog state.
  const [anonymizeOpen, setAnonymizeOpen] = useState(false);
  const [anonPassword, setAnonPassword] = useState("");
  const [anonBusy, setAnonBusy] = useState(false);

  const submitAnonymize = async () => {
    if (anonBusy) return;
    if (!anonPassword) { toast.error("Password is required"); return; }
    setAnonBusy(true);
    try {
      await api.post(`/customers/${id}/anonymize`, { password: anonPassword });
      toast.success("Customer anonymized. Invoicing records retained (7-year tax rule).");
      setAnonymizeOpen(false); setAnonPassword("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setAnonBusy(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [c, bk, inv] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/bookings`, { params: { customer_id: id, page_size: 500 } }),
        api.get(`/invoices`, { params: { customer_id: id, include_excluded_types: true } }),
      ]);
      setCustomer(c.data);
      setBookings(Array.isArray(bk.data) ? bk.data : (bk.data?.items || []));
      setInvoices(Array.isArray(inv.data) ? inv.data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (id) load(); }, [id]); // eslint-disable-line

  const sortedBookings = useMemo(() => {
    const list = [...bookings];
    const { key, dir } = bookingSort;
    list.sort((a, b) => {
      const va = a[key]; const vb = b[key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return dir === "asc" ? va - vb : vb - va;
      }
      return dir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return list;
  }, [bookings, bookingSort]);

  const pagedBookings = useMemo(() => {
    const start = (bookingPage - 1) * PAGE_SIZE;
    return sortedBookings.slice(start, start + PAGE_SIZE);
  }, [sortedBookings, bookingPage]);

  const bookingPages = Math.max(1, Math.ceil(sortedBookings.length / PAGE_SIZE));

  const setBookingSortKey = (key) => {
    setBookingSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );
  };

  if (busy && !customer) {
    return (
      <div className="page" data-testid="customer-profile-loading">
        <div className="text-sm text-muted-foreground mono">// loading…</div>
      </div>
    );
  }
  if (!customer) {
    return (
      <div className="page" data-testid="customer-profile-not-found">
        <div className="text-sm text-muted-foreground">Customer not found.</div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/customers")}>
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to customers
        </Button>
      </div>
    );
  }

  const primaryPhone = (customer.phones || [])[0] || "";
  const primaryEmail =
    (customer.emails || []).find((e) => e?.email)?.email || "";

  return (
    <div className="page space-y-6" data-testid="customer-profile-page">
      <div className="flex items-center gap-3">
        <Link
          to="/customers"
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="customer-profile-back"
        >
          <ArrowLeft className="h-4 w-4" /> Customers
        </Link>
      </div>

      <CustomerDuplicateBanner customerId={customer.id} />
      {customer.is_one_off && (
        <div
          className="bg-violet-50 border border-violet-200 rounded-[10px] p-4 flex items-center justify-between flex-wrap gap-3"
          data-testid="customer-profile-oneoff-banner"
        >
          <div className="text-sm">
            <div className="font-bold text-violet-900">This is a one-off customer (private cash).</div>
            <div className="text-xs text-violet-800/80 mt-0.5">
              Hidden from the main customer list. Promote to a regular customer to keep them in your contacts.
            </div>
          </div>
          <Button
            size="sm"
            className="bg-violet-600 hover:bg-violet-700 text-white gap-2"
            onClick={async () => {
              try {
                await api.put(`/customers/${customer.id}`, { is_one_off: false });
                toast.success(`Promoted ${customer.company_name} to a regular customer`);
                await load();
              } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail));
              }
            }}
            data-testid="customer-profile-promote-button"
          >
            <Star className="h-4 w-4" /> Promote
          </Button>
        </div>
      )}

      {/* Header */}
      <section className="bg-card border border-border rounded-[10px] p-6 space-y-4" data-testid="customer-profile-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Customer 360</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex flex-wrap items-center gap-3">
              <Building2 className="h-7 w-7 text-accent" />
              {customer.company_name || customer.contact_name || <span className="text-muted-foreground italic">(no company name yet)</span>}
              {customer.details_pending && <DetailsPendingBadge />}
            </h1>
            {customer.contact_name && customer.company_name && (
              <div className="mt-1 text-base text-muted-foreground flex items-center gap-2">
                <UserIcon className="h-4 w-4" /> {customer.contact_name}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate(`/customers/${id}/edit`)}
              data-testid="customer-profile-edit"
            >
              <Edit className="h-3 w-3 mr-1" /> Edit customer
            </Button>
            {/* Phase P.18 — GDPR: per-customer export (Art. 20) + anonymise
                (Art. 17) actions. Visible to any manager; anonymise requires
                password step-up server-side. */}
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await api.post(`/customers/${id}/export`, {}, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `customer-${id.slice(0, 8)}.zip`;
                  document.body.appendChild(a);
                  a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Customer export downloaded");
                } catch (e) {
                  toast.error(formatApiError(e?.response?.data?.detail));
                }
              }}
              data-testid="customer-profile-export-gdpr"
            >
              <FileText className="h-3 w-3 mr-1" /> Export
            </Button>
            <Button
              variant="outline"
              onClick={() => setAnonymizeOpen(true)}
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="customer-profile-anonymize-btn"
            >
              Anonymize
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-muted-foreground">
          {primaryPhone && (
            <div className="flex items-center gap-2" data-testid="customer-profile-phone">
              <Phone className="h-4 w-4" /> <span className="mono">{primaryPhone}</span>
            </div>
          )}
          {primaryEmail ? (
            <div className="flex items-center gap-2" data-testid="customer-profile-email">
              <Mail className="h-4 w-4" /> <span className="mono">{primaryEmail}</span>
            </div>
          ) : customer.details_pending ? (
            <div className="flex items-center gap-2 text-red-700" data-testid="customer-profile-email-pending">
              <Mail className="h-4 w-4" /> <span className="italic">No email on file yet</span>
            </div>
          ) : null}
        </div>
      </section>

      {/* Section A — Booking history */}
      <section className="bg-card border border-border rounded-[10px] overflow-hidden" data-testid="customer-profile-bookings">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">// Bookings</div>
            <h2 className="text-lg font-extrabold mt-0.5">
              Booking history · <span className="tabular-nums">{sortedBookings.length}</span>
            </h2>
          </div>
          {bookingPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                disabled={bookingPage <= 1}
                onClick={() => setBookingPage((p) => Math.max(1, p - 1))}
                data-testid="customer-profile-bookings-prev"
              ><ChevronLeft className="h-3 w-3" /></Button>
              <span className="text-sm tabular-nums" data-testid="customer-profile-bookings-page">
                {bookingPage} / {bookingPages}
              </span>
              <Button
                variant="outline" size="sm"
                disabled={bookingPage >= bookingPages}
                onClick={() => setBookingPage((p) => Math.min(bookingPages, p + 1))}
                data-testid="customer-profile-bookings-next"
              ><ChevronRight className="h-3 w-3" /></Button>
            </div>
          )}
        </div>
        {sortedBookings.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="customer-profile-bookings-empty">
            No bookings yet for this customer.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left">
                  <SortHeader label="Date" k="scheduled_start" cur={bookingSort} on={setBookingSortKey} />
                  <SortHeader label="Job template" k="template_name" cur={bookingSort} on={setBookingSortKey} />
                  <SortHeader label="Staff" k="staff_name" cur={bookingSort} on={setBookingSortKey} />
                  <SortHeader label="Total" k="subtotal" cur={bookingSort} on={setBookingSortKey} align="right" />
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Payment</th>
                </tr>
              </thead>
              <tbody>
                {pagedBookings.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => navigate(`/calendar?bookingId=${encodeURIComponent(b.id)}`)}
                    className="border-t border-border hover:bg-secondary/20 cursor-pointer"
                    data-testid={`customer-profile-booking-row-${b.id}`}
                  >
                    <td className="px-4 py-3 mono tabular-nums">
                      {(b.scheduled_start || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3">
                      {b.template_name || (b.line_items?.[0]?.description) || "—"}
                      <div className="text-[11px] mono text-muted-foreground">{b.booking_code}</div>
                    </td>
                    <td className="px-4 py-3">{b.staff_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold">
                      {formatCurrency(b.subtotal ?? 0, customer.currency || "EUR")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={b.payment_status} testid={`customer-profile-booking-payment-${b.id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Section B — Invoices */}
      <section className="bg-card border border-border rounded-[10px] overflow-hidden" data-testid="customer-profile-invoices">
        <div className="px-6 py-4 border-b border-border">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">// Invoices</div>
          <h2 className="text-lg font-extrabold mt-0.5">
            Invoices · <span className="tabular-nums">{invoices.length}</span>
          </h2>
        </div>
        {invoices.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="customer-profile-invoices-empty">
            No invoices have been issued yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left">
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Invoice #</th>
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Issued</th>
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Due</th>
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Amount</th>
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider">Payment</th>
                  <th className="px-4 py-3 mono text-[10px] uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const due = inv.due_date
                    || (inv.issued_at
                      ? new Date(
                          new Date(inv.issued_at).getTime() + 30 * 86400 * 1000
                        ).toISOString().slice(0, 10)
                      : "");
                  return (
                    <tr
                      key={inv.id}
                      className="border-t border-border hover:bg-secondary/20"
                      data-testid={`customer-profile-invoice-row-${inv.id}`}
                    >
                      <td className="px-4 py-3 mono font-bold">{inv.invoice_number}</td>
                      <td className="px-4 py-3 mono tabular-nums text-muted-foreground">
                        {(inv.issued_at || "").slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 mono tabular-nums text-muted-foreground">
                        {(due || "").slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold">
                        {formatCurrency(inv.total ?? 0, inv.currency || "EUR")}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={inv.payment_status} testid={`customer-profile-invoice-payment-${inv.id}`} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => navigate(`/invoices/${inv.id}`)}
                          data-testid={`customer-profile-invoice-view-${inv.id}`}
                        >
                          <FileText className="h-3 w-3 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Phase P.18 — GDPR customer anonymise confirmation. */}
      {anonymizeOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="customer-anonymize-dialog">
          <div className="bg-card border border-border rounded-[10px] max-w-md w-full p-5 space-y-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-rose-700">// GDPR Art. 17</div>
              <h3 className="text-lg font-extrabold tracking-tight mt-1">Anonymize this customer?</h3>
            </div>
            <div className="border border-rose-300 bg-rose-50 rounded-md p-3 text-sm text-rose-900 space-y-1">
              <div>Personal identifiers (name, phones, emails, notes, address) will be replaced with <span className="mono">[anonymized]</span>.</div>
              <div>Invoices &amp; booking numbers stay for the 7-year Irish Revenue retention rule.</div>
              <div className="font-bold">This cannot be undone.</div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" htmlFor="anon-password">Confirm with your password</label>
              <input
                id="anon-password"
                type="password"
                value={anonPassword}
                onChange={(e) => setAnonPassword(e.target.value)}
                autoFocus
                className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="customer-anonymize-password"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setAnonymizeOpen(false); setAnonPassword(""); }} disabled={anonBusy} data-testid="customer-anonymize-cancel">
                Cancel
              </Button>
              <Button
                onClick={submitAnonymize}
                disabled={anonBusy || !anonPassword}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="customer-anonymize-confirm"
              >
                {anonBusy ? "Anonymizing…" : "Anonymize now"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortHeader({ label, k, cur, on, align = "left" }) {
  const active = cur.key === k;
  return (
    <th
      className={
        "px-4 py-3 mono text-[10px] uppercase tracking-wider cursor-pointer select-none " +
        (align === "right" ? "text-right" : "")
      }
      onClick={() => on(k)}
      data-testid={`customer-profile-sort-${k}`}
    >
      {label}
      {active ? (cur.dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}
